"use client";

import { type OnRowClick } from "@kyan/ui";
import { useEffect, useState } from "react";

import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";

export interface UseAutoExpandRowsOptions {
  dataLength: number;
  autoExpandLimit?: number;
  shouldAutoExpand?: boolean;
}

/**
 * Hook to manage auto-expanding rows on first load and manual toggle functionality
 * @param dataLength - The number of data items to potentially expand
 * @param autoExpandLimit - Maximum number of items to auto-expand (default: 50)
 * @param shouldAutoExpand - Whether to auto-expand all rows on first load (default: true)
 */
export const useAutoExpandRows = <T extends object = Record<string, unknown>>({
  dataLength,
  autoExpandLimit = 50,
  shouldAutoExpand = true,
}: UseAutoExpandRowsOptions) => {
  const { market } = usePositionsDrawerMarket();

  const [openRowIdsSet, setOpenRowIdsSet] = useState<Set<string>>(new Set());
  const [hasInteracted, setHasInteracted] = useState(false);

  const openRowIds = Array.from(openRowIdsSet);

  // Reset openRowIdsSet when market changes
  useEffect(() => {
    setOpenRowIdsSet(new Set());
    setHasInteracted(false);
  }, [market]);

  useEffect(() => {
    if (
      shouldAutoExpand &&
      !hasInteracted &&
      openRowIdsSet.size === 0 &&
      Boolean(dataLength) &&
      dataLength <= autoExpandLimit
    ) {
      setOpenRowIdsSet(
        new Set(Array.from({ length: dataLength }, (_, i) => i.toString())),
      );
    }
  }, [
    openRowIdsSet.size,
    dataLength,
    hasInteracted,
    autoExpandLimit,
    shouldAutoExpand,
  ]);

  const onRowClick: OnRowClick<T> = ({ rowId }) => {
    setHasInteracted(true);
    setOpenRowIdsSet((previous) => {
      const newSet = new Set(previous);

      newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId);

      return newSet;
    });
  };

  return {
    openRowIds,
    onRowClick,
  };
};
